"use client";

import Link from "next/link";
import { useState, useEffect } from "react";
import type { UserProfile } from "@/lib/supabase/queries";

const DISMISS_KEY = "profileBannerDismissed";

function countFilledFields(profile: UserProfile): number {
  let count = 0;
  if (profile.display_name) count++;
  if (profile.avatar_url) count++;
  if (profile.bio) count++;
  if (profile.platform_preferences && profile.platform_preferences.length > 0) count++;
  return count;
}

interface ProfileCompletionBannerProps {
  profile: UserProfile;
}

export function ProfileCompletionBanner({ profile }: ProfileCompletionBannerProps) {
  const [dismissed, setDismissed] = useState(true); // start hidden to avoid flash

  useEffect(() => {
    setDismissed(localStorage.getItem(DISMISS_KEY) === "true");
  }, []);

  const hasBio = !!profile.bio;
  const hasPlatforms = !!profile.platform_preferences && profile.platform_preferences.length > 0;

  // Auto-hide when bio + platforms are filled
  if (hasBio && hasPlatforms) return null;
  if (dismissed) return null;

  const filled = countFilledFields(profile);
  const progressPercent = (filled / 4) * 100;

  function handleDismiss() {
    localStorage.setItem(DISMISS_KEY, "true");
    setDismissed(true);
  }

  return (
    <section className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-4">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-amber-100">
          <span
            className="material-symbols-outlined text-xl text-amber-700"
            style={{ fontVariationSettings: "'FILL' 1" }}
          >
            edit_note
          </span>
        </div>
        <div className="flex-1 space-y-1">
          <h3 className="text-sm font-semibold text-amber-900">Completez votre profil</h3>
          <p className="text-xs text-amber-800/70">
            Ajoutez votre bio et vos plateformes pour personnaliser vos annonces
          </p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-3 space-y-1.5">
        <div className="h-1.5 rounded-full bg-amber-200">
          <div
            className="h-1.5 rounded-full bg-amber-600 transition-all duration-500"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-amber-700">{filled} sur 4 completes</span>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDismiss}
              className="text-[11px] text-amber-700 underline underline-offset-2 transition-colors hover:text-amber-900"
            >
              Plus tard
            </button>
            <Link
              href="/profile/edit"
              className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-amber-700 active:scale-95"
            >
              Completer
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
